import { cva, type VariantProps } from 'class-variance-authority'
import React, { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react'
import { cn } from './cn'
import { Spinner } from './Spinner'

const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap',
    'rounded-pill border border-solid border-transparent font-text font-semibold no-underline select-none',
    'transition-[background,box-shadow,filter,transform,border-color] duration-150 ease-out',
    'active:scale-[0.98] motion-reduce:active:scale-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-[image:var(--plug-button)] bg-origin-border text-on-accent shadow-[var(--plug-button-inset),var(--plug-button-glow)] enabled:hover:[filter:var(--plug-button-hover)]',
        secondary:
          'border-[var(--plug-secondary-border)] bg-[var(--plug-secondary)] text-fg enabled:hover:border-[var(--plug-secondary-border-hover)] enabled:hover:bg-[var(--plug-secondary-hover)]',
        ghost: 'bg-transparent text-fg enabled:hover:bg-[var(--plug-ghost-hover)]',
        danger: 'bg-danger text-on-danger enabled:hover:brightness-110',
        /** Tinted pair for directional actions (buy/sell, long/short). */
        'tint-success':
          'border-success/45 bg-success/20 text-fg [&_svg]:text-success enabled:hover:border-success/70 enabled:hover:bg-success/28',
        'tint-danger':
          'border-danger/45 bg-danger/20 text-fg [&_svg]:text-danger enabled:hover:border-danger/70 enabled:hover:bg-danger/28',
        link: 'h-auto! bg-transparent px-0! text-link underline-offset-4 enabled:hover:underline',
      },
      size: {
        sm: 'h-9 px-3.5 text-[13px]',
        md: 'h-12 px-[22px] text-[15px]',
        lg: 'h-14 px-7 text-[16px]',
      },
      fullWidth: { true: 'flex w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
}

/** One primary per view. Stacked buttons sit 16px apart (`gap-4`). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      fullWidth,
      loading,
      startIcon,
      endIcon,
      disabled,
      className,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...props}
    >
      {loading ? <Spinner size={size === 'sm' ? 16 : 20} /> : startIcon}
      {children}
      {!loading && endIcon}
    </button>
  )
)
Button.displayName = 'Button'
